<script setup lang="ts">
import { TABLE_CELL_TYPE } from "./useTable";
import { useTableCell } from "./useTableCell";
import type { TableCellProps } from "./TableCellProps";

/**
 * **TableCell** (`e-table-cell`)
 *
 * Renders a data cell within a `<e-table-row>` inside `<e-table-body>`.
 * Supports text overflow, alignment, colspan, and rowspan via props.
 *
 * This component is presentation-only — it holds no local state or logic.
 * Props are registered in the shared stateStore so parent components
 * (e.g. sorting in `useTable`) can read cell values.
 *
 * @example
 * ```html
 * <!-- Basic cell -->
 * <e-table-cell>Hello</e-table-cell>
 *
 * <!-- Cell with alignment -->
 * <e-table-cell align="right">$99.00</e-table-cell>
 *
 * <!-- Cell with text truncation -->
 * <e-table-cell text-overflow="truncate">Long text content...</e-table-cell>
 *
 * <!-- Cell with sortable value attribute (used by table sorting) -->
 * <e-table-cell value="99">$99.00</e-table-cell>
 * ```
 */
defineOptions({
  name: TABLE_CELL_TYPE,
  inheritAttrs: true,
});

const props = withDefaults(defineProps<TableCellProps>(), {
  textOverflow: "wrap",
  colspan: 1,
  rowspan: 1,
  align: "left",
  valign: "middle",
});

const { classes } = useTableCell({ props });
</script>

<style lang="css">
:host {
  display: contents;
}
</style>

<template>
  <div :class="classes" part="cell">
    <slot />
  </div>
</template>
